/**
 * Protected Route Component
 * Wrapper component that protects routes based on authentication and permissions
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Protected Route Component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string[]} props.requiredPermissions - Required permissions to access the route
 * @param {string} props.requiredRole - Required role to access the route
 * @param {string} props.redirectTo - Path to redirect to if not authorized
 * @param {boolean} props.requireAuth - Whether authentication is required (default: true)
 */
const ProtectedRoute = ({
  children,
  requiredPermissions = [],
  requiredRole = null,
  redirectTo = '/login',
  requireAuth = true,
}) => {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const { can } = usePermissions();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Check if authentication is required and user is not authenticated
  if (requireAuth && !isAuthenticated && !isLoading) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  // Check permission requirements
  if (requiredPermissions.length > 0) {
    const hasAllPermissions = requiredPermissions.every(permission =>
      can(permission)
    );

    if (!hasAllPermissions) {
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }
  }

  // User is authorized, render children
  return children;
};

/**
 * Public Route Component
 * Redirects authenticated users away from public routes (like login)
 */
export const PublicRoute = ({
  children,
  redirectTo = '/dashboard',
  redirectIfAuthenticated = true,
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Redirect authenticated users if specified
  if (redirectIfAuthenticated && isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

/**
 * Role-based Route Component
 * Protects routes based on user roles
 */
export const RoleRoute = ({
  children,
  allowedRoles = [],
  redirectTo = '/unauthorized',
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has allowed role
  const userRole = user?.role;
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

/**
 * Permission-based Route Component
 * Protects routes based on user permissions
 */
export const PermissionRoute = ({
  children,
  requiredPermissions = [],
  requireAll = true,
  redirectTo = '/unauthorized',
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { can } = usePermissions();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions
  if (requiredPermissions.length > 0) {
    const hasRequiredPermissions = requireAll
      ? requiredPermissions.every(permission => can(permission))
      : requiredPermissions.some(permission => can(permission));

    if (!hasRequiredPermissions) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
